// In FilterNavbar component
import { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import FilterNavbarCategories from './FilterNavbarCategories';
import SpecialsToggle from './SpecialsToggle';
import PricingType from '../Cart/PricingType';
import OpenOrClosed from './OpenOrClosed';
import { useConfig } from '../../contexts/ConfigContext';
import { useAppState } from '../../contexts/AppStateContext';
import { useFilter } from '../../contexts/FilterContext';
import { useCart } from "../../contexts/CartContext";
import { useRetailerData } from '../../hooks/dutchie-queries.js';
import { processCategoriesToShow } from '../../utils/filter/filterProcessors';
import SearchBar from './SearchBar.jsx';

const FilterNavbar = ({
    forcedOrderType,
    categoriesToShow = [],
    mobileFilterButtons = false,
}) => {
    const { retailerId } = useAppState();
    const { menuFilter, styleOptions, retailers } = useConfig();
    
    // Compute menuSlug from context
    const menuSlug = retailers[retailerId]?.menuSlug;
    const {
        selectedFilters,
        setSelectedFilters,
        handleFilterChange,
        handleAllClick,
        setSearchFilter,
        mobileFilterOpen,
        setMobileFilterOpen,
        filterTerms,
        specialsExist
    } = useFilter();
    const { data: retailerData, isFetched: isFetchedRetailerData } = useRetailerData(retailerId);
    const { cartData, isFetched: isFetchedCartData } = useCart();

    const isMobile = useMediaQuery({ query: '(max-width: 991px)' });

    // Process categories the same way as in Filter.jsx
    const processedCategories = useMemo(() => {
        return processCategoriesToShow(categoriesToShow, filterTerms?.categories);
    }, [categoriesToShow, filterTerms?.categories]);

    // Simple handler for toggling mobile filters
    const handleToggleFilters = () => {
        setMobileFilterOpen(prev => !prev);
        document.body.classList.toggle('lock-scroll', !mobileFilterOpen);
    };

    // Mobile filter toggle button component
    const FilterToggleIcon = ({ onToggle }) => (
        <button
            className="navbar-top-filter-icon"
            onClick={onToggle}
            aria-label="Toggle filters"
        >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="M3.9 54.9C10.5 40.9 24.5 32 40 32H472c15.5 0 29.5 8.9 36.1 22.9s4.6 30.5-5.2 42.5L320 320.9V448c0 12.1-6.8 23.2-17.7 28.6s-23.8 4.3-33.5-3l-64-48c-8.1-6-12.8-15.5-12.8-25.6V320.9L9 97.3C-.7 85.4-2.8 68.8 3.9 54.9z" />
            </svg>
        </button>
    );

    return (
        <>
            <nav
                className={`filter-navbar ${styleOptions?.menuNavbarClasses || ''}`}
                aria-label="Shop navigation"
            >
                <div className="filter-navbar-container">
                    {/* Mobile Search Bar */}
                    <div className="filter-navbar-search">
                        <SearchBar />
                    </div>
                    {/* Main navbar content */}
                    <div className="filter-navbar-main">
                        {/* Left side content */}
                        <div className="filter-navbar-left">
                            {isMobile && !mobileFilterButtons && (
                                <FilterToggleIcon onToggle={handleToggleFilters} />
                            )}

                            <FilterNavbarCategories
                                filters={processedCategories.length > 0 ? processedCategories : filterTerms?.categories || []}
                                selectedFilters={selectedFilters}
                                setSelectedFilters={setSelectedFilters}
                                menuSlug={menuSlug}
                                specialsExist={specialsExist}
                                handleChange={handleFilterChange}
                                handleAllClick={handleAllClick}
                                menuFilter={menuFilter}
                            />
                        </div>

                        {/* Right side content */}
                        <div className="filter-navbar-right">
                            {isFetchedRetailerData && <OpenOrClosed />}
                            {isFetchedCartData && <PricingType forcedOrderType={forcedOrderType} />}
                        </div>
                    </div>
                </div>
            </nav>
            
            {/* Mobile filter buttons */}
            {mobileFilterButtons && (
                <div className='mobile-filter-additional-buttons'>
                    <div className='mobile-filter-additional-specials'>
                        <SpecialsToggle
                            selectedFilters={selectedFilters}
                            handleChange={handleFilterChange}
                            deviceType="mobile"
                        />
                    </div>
                    <div
                        className='mobile-filter-all-filters'
                        onClick={handleToggleFilters}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleToggleFilters();
                            }
                        }}
                        role="button"
                        tabIndex="0"
                        aria-label="Open filters"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                            <path d="M3.9 54.9C10.5 40.9 24.5 32 40 32H472c15.5 0 29.5 8.9 36.1 22.9s4.6 30.5-5.2 42.5L320 320.9V448c0 12.1-6.8 23.2-17.7 28.6s-23.8 4.3-33.5-3l-64-48c-8.1-6-12.8-15.5-12.8-25.6V320.9L9 97.3C-.7 85.4-2.8 68.8 3.9 54.9z" />
                        </svg>
                        <span>More Filters</span>
                    </div>
                </div>
            )}
        </>
    );
};

export default FilterNavbar;
